import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { saveTenant, sendTenantPasswordLink } from "../../actions/tenant";
import {
  fieldClass,
  labelClass,
  primaryButton,
  secondaryButton,
  smallButton,
} from "../template/templateLabels";
import { sinceDay } from "./tenantLabels";

const today = () => format(new Date(), "yyyy-MM-dd");

// Adds a tenant or edits one: their name, email, and the kiosks they rent, each
// from a day; their report starts there. A kiosk with no day counts all its
// payments. No one sets a tenant's password here: a new tenant is mailed an
// invite, and one who forgot theirs a link to set a new one.
const TenantForm = ({ tenant, printers, tenants, onClose, saveTenant, sendTenantPasswordLink }) => {
  const isNew = !tenant;
  const [name, setName] = useState(isNew ? "" : tenant.name);
  const [email, setEmail] = useState(isNew ? "" : tenant.email);
  // [{ printerId, since: "YYYY-MM-DD" or "" }] in the order they were given
  const [kiosks, setKiosks] = useState(
    isNew
      ? []
      : tenant.printers.map((item) => ({
          printerId: item.printerId,
          since: sinceDay(item.since) || "",
        }))
  );
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);

  // Who else rents each kiosk, to see it before giving it twice
  const others = useMemo(() => {
    const byPrinter = {};
    tenants
      .filter((item) => isNew || item._id !== tenant._id)
      .forEach((item) =>
        item.printers.forEach(({ printerId }) => {
          byPrinter[printerId] = [...(byPrinter[printerId] || []), item.name];
        })
      );
    return byPrinter;
  }, [tenants, tenant, isNew]);

  const selected = (printerId) => kiosks.find((item) => item.printerId === printerId);
  const toggle = (printerId) =>
    setKiosks(
      selected(printerId)
        ? kiosks.filter((item) => item.printerId !== printerId)
        : [...kiosks, { printerId, since: today() }]
    );
  const setSince = (printerId, since) =>
    setKiosks(kiosks.map((item) => (item.printerId === printerId ? { ...item, since } : item)));

  const term = search.trim().toLowerCase();
  const shown = printers.filter(
    (printer) =>
      term === "" ||
      `${printer.name} ${printer.location || ""}`.toLowerCase().includes(term)
  );
  // A kiosk given before that is not in the list any more
  const missing = kiosks.filter(
    (item) => !printers.some((printer) => printer._id === item.printerId)
  );

  const nameValid = name.trim() !== "";
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const kiosksValid = kiosks.length > 0 && missing.length === 0;
  const busy = submitting || sending;
  const canSubmit = !busy && nameValid && emailValid && kiosksValid;
  // A link goes to the saved email, and the backend stops the old one when it
  // changes
  const emailChanged = !isNew && email.trim().toLowerCase() !== tenant.email;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrors([]);
    const body = {
      name: name.trim(),
      email: email.trim(),
      printers: kiosks.map(({ printerId, since }) => ({ printerId, since })),
    };
    const result = await saveTenant(isNew ? null : tenant._id, body);
    if (result.tenant) {
      // Their invite went to the old address; it goes to the new one instead
      if (emailChanged && tenant.pending) await sendTenantPasswordLink(tenant._id);
      onClose();
    } else {
      setErrors(result.errors.map((error) => error.msg));
      setSubmitting(false);
    }
  };

  const sendLink = async () => {
    setSending(true);
    await sendTenantPasswordLink(tenant._id);
    setSending(false);
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    // Only a press on the backdrop itself closes the sheet
    <div
      className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        onSubmit={submit}
        className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-2xl"
      >
        <header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
              {isNew ? "Шинэ түрээслэгч" : "Түрээслэгч засах"}
            </p>
            {!isNew && (
              <p className="text-sm text-gray-600 truncate dark:text-gray-400">{tenant.email}</p>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
            aria-label="close"
          >
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="min-h-0 px-6 py-4 space-y-4 overflow-y-auto">
          {errors.length > 0 && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md dark:bg-red-900 dark:text-red-100">
              {errors.map((message, i) => (
                <p key={i}>{message}</p>
              ))}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className={labelClass}>Нэр</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                autoFocus
                className={fieldClass}
              />
            </label>
            <label className="block">
              <span className={labelClass}>Имэйл</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                autoComplete="off"
                className={fieldClass}
              />
              {email !== "" && !emailValid && (
                <span className="block mt-1 text-xs text-red-600 dark:text-red-400">
                  Имэйл хаяг буруу байна.
                </span>
              )}
            </label>
          </div>

          {isNew ? (
            <p className="flex items-start p-3 text-xs text-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
              <i className="mt-0.5 mr-2 fas fa-envelope" />
              Энэ имэйл рүү урилга очно. Түрээслэгч урилгын холбоосоор нууц үгээ
              өөрөө үүсгэнэ. Нэвтэрсний дараа зөвхөн өөрт оноосон киоскуудын
              тайланг харна.
            </p>
          ) : (
            <div>
              <span className={labelClass}>Нууц үг</span>
              <div className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg dark:border-gray-600">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {tenant.pending
                    ? "Түрээслэгч урилгаа нээж нууц үгээ үүсгээгүй байна."
                    : "Нууц үгийг зөвхөн түрээслэгч өөрөө үүсгэнэ. Сэргээх холбоос түүний имэйл рүү очно."}
                </p>
                <button
                  type="button"
                  onClick={sendLink}
                  disabled={busy || emailChanged}
                  className={
                    smallButton +
                    " inline-flex items-center flex-shrink-0 whitespace-nowrap" +
                    (busy || emailChanged ? " opacity-50 cursor-not-allowed" : "")
                  }
                >
                  <i className="mr-1.5 fas fa-envelope" />
                  {sending
                    ? "Илгээж байна..."
                    : tenant.pending
                    ? "Урилга дахин илгээх"
                    : "Нууц үг сэргээх"}
                </button>
              </div>
              {emailChanged && (
                <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {tenant.pending
                    ? "Хадгалахад урилга шинэ хаяг руу очно."
                    : "Шинэ хаягийг хадгалсны дараа сэргээх холбоос илгээх боломжтой."}
                </span>
              )}
            </div>
          )}

          <fieldset>
            <div className="flex items-end justify-between gap-3 mb-2">
              <legend className={labelClass + " mb-0"}>Киоскууд</legend>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {kiosks.length} сонгосон
              </span>
            </div>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Киоск хайх"
              className={fieldClass + " mb-2"}
            />
            {missing.length > 0 && (
              <p className="mb-2 text-xs text-yellow-700 dark:text-yellow-300">
                Оноосон {missing.length} киоск системээс устгагдсан байна.
                <button
                  type="button"
                  onClick={() => setKiosks(kiosks.filter((item) => !missing.includes(item)))}
                  className="ml-2 font-semibold underline"
                >
                  Жагсаалтаас хасах
                </button>
              </p>
            )}
            <div className="overflow-y-auto border border-gray-200 divide-y divide-gray-100 rounded-lg max-h-72 dark:border-gray-600 dark:divide-gray-700">
              {shown.length === 0 && (
                <p className="px-3 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
                  Киоск олдсонгүй.
                </p>
              )}
              {shown.map((printer) => {
                const kiosk = selected(printer._id);
                const taken = others[printer._id];
                return (
                  <div
                    key={printer._id}
                    className={
                      "flex flex-wrap items-center gap-3 px-3 py-2 " +
                      (kiosk ? "bg-purple-50 dark:bg-gray-700" : "")
                    }
                  >
                    <label className="flex items-center flex-1 min-w-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(kiosk)}
                        onChange={() => toggle(printer._id)}
                        disabled={submitting}
                        className="text-purple-600 rounded focus:ring-purple-200 focus:ring-opacity-50 focus:ring"
                      />
                      <span className="min-w-0 ml-3">
                        <span className="block text-sm font-medium text-gray-700 truncate dark:text-gray-200">
                          {printer.name}
                        </span>
                        <span className="block text-xs text-gray-500 truncate dark:text-gray-400">
                          {printer.location || "Байршилгүй"}
                          {taken && ` · Бусад: ${taken.join(", ")}`}
                        </span>
                      </span>
                    </label>
                    {kiosk && (
                      <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        Хэзээнээс
                        <input
                          type="date"
                          value={kiosk.since}
                          onChange={(e) => setSince(printer._id, e.target.value)}
                          disabled={submitting}
                          title="Хоосон бол киоскийн бүх хугацааны гүйлгээ тоологдоно"
                          className="h-8 px-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600"
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Тайлан киоск бүрийн оноосон өдрөөс эхэлнэ. Огноо хоосон бол бүх
              хугацааны гүйлгээ тоологдоно.
            </p>
          </fieldset>
        </div>

        <footer className="flex justify-end flex-shrink-0 px-6 py-3 space-x-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <button type="button" onClick={close} disabled={busy} className={secondaryButton}>
            Болих
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={primaryButton + (canSubmit ? "" : " opacity-50 cursor-not-allowed")}
          >
            {submitting
              ? isNew
                ? "Илгээж байна..."
                : "Хадгалж байна..."
              : isNew
              ? "Урилга илгээх"
              : "Хадгалах"}
          </button>
        </footer>
      </form>
    </div>
  );
};

TenantForm.propTypes = {
  tenant: PropTypes.object,
  printers: PropTypes.array.isRequired,
  tenants: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  saveTenant: PropTypes.func.isRequired,
  sendTenantPasswordLink: PropTypes.func.isRequired,
};

export default connect(null, { saveTenant, sendTenantPasswordLink })(TenantForm);
